import { useState } from 'react';
import { useLocation } from 'wouter';
import { Decimal } from 'decimal.js';
import { useAuth } from '../../hooks/useAuth';
import AccessDenied from '../../components/AccessDenied';
import {
  useOrders,
  SOURCE_TYPE_LABELS,
  ORDER_TYPE_LABELS,
  type CreateCustomerOrderDto,
  type CreateOrderProjectDto,
} from '../../hooks/useOrders';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '../../components/business/AssociationLookupSelector';
// Issue #345 audit fix2: 与 OrderList 的入口按钮以及 OrderController 后端守卫
// 保持一致——必须使用规范的 `crm.order:create` 字符串（resource.namespace + 冒号
// + action），而不是先前的扁平字面量 `crm:order:create`。后者会让 hasPermission()
// 把整个字符串当成 `crm` 资源 + `order:create` 行为，跟后端 RBAC 谓词不匹配，
// 导致拿到正确权限的市场负责人/超级管理员之外的账号在列表页可见「新建订单」
// 入口，却在 /crm/orders/create 被前端 AccessDenied 拒绝。
import { CRM_PERMISSIONS, hasPermission } from '@/lib/crmPermissions';

const SOURCE_TYPE_LIST = Object.keys(SOURCE_TYPE_LABELS) as string[];
const ORDER_TYPE_LIST = Object.keys(ORDER_TYPE_LABELS) as string[];

const DECIMAL_INPUT_RE = /^-?\d+(\.\d+)?$/;
const ZERO = new Decimal(0);

function parseDecimalString(raw: string): Decimal | null {
  const trimmed = raw.trim();
  if (!trimmed || !DECIMAL_INPUT_RE.test(trimmed)) return null;
  try {
    return new Decimal(trimmed);
  } catch {
    return null;
  }
}

interface ProjectFormRow {
  serviceUserId: string;
  projectName: string;
  projectCode: string;
  unitPrice: string;
  quantity: string;
  notes: string;
}

const emptyProject = (): ProjectFormRow => ({
  serviceUserId: '',
  projectName: '',
  projectCode: '',
  unitPrice: '',
  quantity: '1',
  notes: '',
});

export function CreateOrder() {
  const [, navigate] = useLocation();
  const { isAuthenticated, permissions } = useAuth();
  const { loading, error, createOrder } = useOrders();

  const canCreateOrder = hasPermission(
    permissions,
    CRM_PERMISSIONS.ORDER_CREATE,
  );

  // P1-6: Permission gate — backend OrderController enforces crm.order:create.
  if (!isAuthenticated || !canCreateOrder) {
    return <AccessDenied />;
  }

  const [customer, setCustomer] = useState<SelectedAssociation | null>(null);
  const [title, setTitle] = useState('');
  const [orderType, setOrderType] = useState('individual');
  const [sourceType, setSourceType] = useState('');
  const [assignedUser, setAssignedUser] = useState<SelectedAssociation | null>(null);
  const [notes, setNotes] = useState('');
  const [projects, setProjects] = useState<ProjectFormRow[]>([emptyProject()]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateProject = (index: number, field: keyof ProjectFormRow, value: string) => {
    setProjects(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const addProject = () => setProjects(prev => [...prev, emptyProject()]);
  const removeProject = (index: number) => setProjects(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!customer?.id) {
      setSubmitError('请选择客户');
      return;
    }
    if (!title.trim()) {
      setSubmitError('请输入订单标题');
      return;
    }
    if (projects.length === 0) {
      setSubmitError('至少需要一个订单项目');
      return;
    }
    for (const p of projects) {
      if (!p.serviceUserId.trim()) {
        setSubmitError('每个项目必须填写服务用户 ID');
        return;
      }
      if (!p.projectName.trim()) {
        setSubmitError('每个项目必须填写项目名称');
        return;
      }
      // Decimal-safe validation. unitPrice/quantity stay as strings end to end.
      const unitPrice = parseDecimalString(p.unitPrice);
      if (!unitPrice || unitPrice.lessThan(ZERO)) {
        setSubmitError('单价必须为有效数字');
        return;
      }
      const quantity = parseDecimalString(p.quantity);
      if (!quantity || quantity.lessThanOrEqualTo(ZERO)) {
        setSubmitError('数量必须为正数');
        return;
      }
    }

    // P1-5: Pass as decimal strings to avoid precision loss
    const projectDtos: CreateOrderProjectDto[] = projects.map(p => ({
      serviceUserId: p.serviceUserId.trim(),
      projectName: p.projectName.trim(),
      projectCode: p.projectCode.trim() || undefined,
      unitPrice: p.unitPrice.trim(),
      quantity: p.quantity.trim(),
      notes: p.notes.trim() || undefined,
    }));

    const dto: CreateCustomerOrderDto = {
      customerId: customer.id,
      title: title.trim(),
      orderType,
      sourceType: sourceType || undefined,
      assignedUserId: assignedUser?.id ?? undefined,
      notes: notes.trim() || undefined,
      projects: projectDtos,
    };

    try {
      const created = await createOrder(dto);
      navigate(`/crm/orders/${created.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create order');
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/crm/orders')}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← 返回列表
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">新建订单</h1>
      </div>

      {(error || submitError) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {submitError ?? error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-base font-medium text-gray-900 mb-4">基本信息</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                客户 <span className="text-red-500">*</span>
              </label>
              <AssociationLookupSelector
                kind="customer"
                value={customer}
                onChange={setCustomer}
                placeholder="按姓名搜索客户…"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                订单标题 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="请输入订单标题"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">订单类型</label>
              <select
                value={orderType}
                onChange={e => setOrderType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {ORDER_TYPE_LIST.map(t => (
                  <option key={t} value={t}>{ORDER_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">来源类型</label>
              <select
                value={sourceType}
                onChange={e => setSourceType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">请选择</option>
                {SOURCE_TYPE_LIST.map(t => (
                  <option key={t} value={t}>{SOURCE_TYPE_LABELS[t]}</option>
                ))}
              </select>
              {(sourceType === 'natural_visit' || sourceType === 'online_lead') && (
                <p className="mt-1 text-xs text-orange-600">
                  该来源类型将自动标记为高风险客户
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">负责人</label>
              {/*
                customer_orders.assigned_user_id is a system user reference.
                kind="user" returns users.id; kind="employee" would return
                hr_employees.id and break order assignment scope checks.
                Issue #344 P1 fix.
              */}
              <AssociationLookupSelector
                kind="user"
                value={assignedUser}
                onChange={setAssignedUser}
                placeholder="搜索负责人姓名…"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="可选"
              />
            </div>
          </div>
        </div>

        {/* Projects */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium text-gray-900">订单项目</h2>
            <button
              type="button"
              onClick={addProject}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              + 添加项目
            </button>
          </div>
          <div className="space-y-4">
            {projects.map((project, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">项目 {index + 1}</span>
                  {projects.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeProject(index)}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      删除
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      服务用户 ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={project.serviceUserId}
                      onChange={e => updateProject(index, 'serviceUserId', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      placeholder="服务用户 ID"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      项目名称 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={project.projectName}
                      onChange={e => updateProject(index, 'projectName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      placeholder="项目名称"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">项目编码</label>
                    <input
                      type="text"
                      value={project.projectCode}
                      onChange={e => updateProject(index, 'projectCode', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      placeholder="可选"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      单价 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={project.unitPrice}
                      onChange={e => updateProject(index, 'unitPrice', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      数量 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0.000001"
                      step="any"
                      value={project.quantity}
                      onChange={e => updateProject(index, 'quantity', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/crm/orders')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={loading || !canCreateOrder}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? '创建中...' : '创建订单'}
          </button>
        </div>
      </form>
    </div>
  );
}
